/**
 * Output Mapper — Resolves downstream step inputs from upstream outputs.
 *
 * Reads DAG edges to wire upstream outputs to downstream inputs, with
 * type coercion for compatible WorkflowTypes and workspace-state fallback
 * when step results don't contain the expected output.
 *
 * @module gibbs/workflow/outputMapper
 */

import { getAction, isTypeCompatible } from '../registry/index';

// ─── Workspace Fallback Map ────────────────────────────────────────────────

/**
 * Maps output/input names to workspace accessor functions.
 * Used when an edge's output is not present in step results.
 *
 * @type {Object<string, function(Object): *>}
 */
const workspaceFallbacks = {
    compounds: (ws) => ws.getActiveCompound?.(),
    compound: (ws) => ws.getActiveCompound?.(),
    caseData: (ws) => ws.firstCaseData?.(),
    protein: (ws) => ws.firstCaseData?.(),
    // fragmentMap / fragments: intentionally null. There is no Workspace-
    // level getFragmentMap() method (the old fallback was a phantom call
    // that always returned undefined). Fragment info lives per-CaseData
    // via caseData.getAvailableFragmentInfo(), and a "fragment map" is
    // produced by the load-fragment-map action — if a workflow step
    // needs one and no upstream step produced it, the correct behavior
    // is to fail fast with "required input 'fragmentMap' could not be
    // resolved" so the user knows to add a load-fragment-map step.
    fragmentMap: () => null,
    fragments: () => null,
};

// ─── Type Coercion ─────────────────────────────────────────────────────────

/**
 * Coercion rules for compatible types that require value transformation.
 * Each entry: [outputType, inputType] → transform function.
 *
 * @type {Array<{outputType: string, inputType: string, coerce: function(*): *}>}
 */
const coercionRules = [
    {
        outputType: 'Compound',
        inputType: 'CompoundList',
        coerce: (value) => (Array.isArray(value) ? value : [value]),
    },
    {
        outputType: 'SDF',
        inputType: 'MolData',
        coerce: (value) => value, // Format subsumption, no transform needed
    },
    {
        outputType: 'PDB',
        inputType: 'MolData',
        coerce: (value) => value,
    },
    {
        outputType: 'CIF',
        inputType: 'MolData',
        coerce: (value) => value,
    },
    {
        outputType: 'SMILES',
        inputType: 'MolData',
        coerce: (value) => value,
    },
    {
        outputType: 'ProteinStructure',
        inputType: 'PDB',
        coerce: (value) => value,
    },
    {
        outputType: 'PdbData',
        inputType: 'PDB',
        coerce: (value) => value,
    },
];

/**
 * Apply type coercion if needed for a value being mapped from an output
 * type to an input type.
 *
 * @param {*} value - The raw output value
 * @param {string} outputType - The type produced by the source action
 * @param {string} inputType - The type expected by the target action
 * @returns {*} The coerced value
 */
function applyCoercion(value, outputType, inputType) {
    if (outputType === inputType) return value;

    const rule = coercionRules.find(
        (r) => r.outputType === outputType && r.inputType === inputType
    );
    return rule ? rule.coerce(value) : value;
}

// ─── Core Resolver ─────────────────────────────────────────────────────────

/**
 * Resolve inputs for a target node by reading DAG edges, extracting
 * upstream outputs from step results, and falling back to workspace state.
 *
 * @param {import('./Workflow.js').WorkflowNode} targetNode - Node being prepared for execution
 * @param {import('./Workflow.js').Workflow} workflow - Workflow DAG instance
 * @param {Map<string, Object>} stepResults - Map of nodeId → result object from completed steps
 * @param {Object} workspace - Current workspace state (getActiveCompound, firstCaseData, etc.)
 * @returns {{ resolvedInputs: Object<string, *>, errors: string[] }}
 */
export function resolveInputs(targetNode, workflow, stepResults, workspace) {
    const resolvedInputs = {};
    const errors = [];

    const targetAction = getAction(targetNode.actionId);
    if (!targetAction) {
        errors.push(`Unknown action "${targetNode.actionId}" for node "${targetNode.id}".`);
        return { resolvedInputs, errors };
    }

    const incomingEdges = workflow.getIncomingEdges(targetNode.id);

    // ── Step 1: Wire each incoming edge ─────────────────────────────────
    for (const edge of incomingEdges) {
        const sourceNode = workflow.getNode(edge.sourceNodeId);
        if (!sourceNode) {
            errors.push(
                `Edge "${edge.id}": source node "${edge.sourceNodeId}" not found in workflow.`
            );
            continue;
        }

        const sourceAction = getAction(sourceNode.actionId);
        const sourceResult = stepResults.get(edge.sourceNodeId);

        let value;
        let resolved = false;

        // Try extracting the named output from step results
        if (sourceResult != null && edge.outputName in sourceResult) {
            value = sourceResult[edge.outputName];
            resolved = true;
        }

        // Workspace fallback if output not in step results
        if (!resolved && workspace) {
            const fallback = workspaceFallbacks[edge.outputName];
            if (fallback) {
                const fallbackValue = fallback(workspace);
                if (fallbackValue != null) {
                    value = fallbackValue;
                    resolved = true;
                }
            }
        }

        if (resolved) {
            // Apply type coercion if source and target types differ
            const outputDef = sourceAction?.outputs.find((o) => o.name === edge.outputName);
            const inputDef = targetAction.inputs.find((i) => i.name === edge.inputName);

            if (outputDef && inputDef && outputDef.type !== inputDef.type) {
                if (isTypeCompatible(outputDef.type, inputDef.type)) {
                    value = applyCoercion(value, outputDef.type, inputDef.type);
                }
            }

            resolvedInputs[edge.inputName] = value;
        }
    }

    // ── Step 2: paramOverrides fallback for inputs without upstream edges.
    // When a plan is built via builders.js, both `params` and `resolvedInputs`
    // from the ActionPlan are merged into node.paramOverrides. For inputs
    // that are literal values (not derived from prior steps and not covered
    // by workspaceFallbacks), this is the only place the executor can find
    // them — e.g. `pdbId: "6HTX"` on load-protein-from-pdb. Without this
    // step, any multi-step workflow with a literal input would fail at
    // resolution time with "required input X could not be resolved".
    const overrides = targetNode.paramOverrides || {};
    for (const inputDef of targetAction.inputs) {
        if (inputDef.name in resolvedInputs) continue;
        if (inputDef.name in overrides && overrides[inputDef.name] != null) {
            resolvedInputs[inputDef.name] = overrides[inputDef.name];
        }
    }

    // ── Step 3: Check for unresolved required inputs ────────────────────
    for (const inputDef of targetAction.inputs) {
        if (inputDef.required && !(inputDef.name in resolvedInputs)) {
            // Last-chance workspace fallback for inputs not covered by edges
            // or paramOverrides
            if (workspace) {
                const fallback = workspaceFallbacks[inputDef.name];
                if (fallback) {
                    const fallbackValue = fallback(workspace);
                    if (fallbackValue != null) {
                        // Substituting the active compound for an input that an
                        // edge was supposed to supply is how a plumbing bug becomes
                        // a plausible wrong answer: the step runs, over one
                        // compound, and everything downstream reports zeros for the
                        // rest. It stays a fallback — the async-job dispatch path
                        // and several ActionDef/handler pairs are unaudited, so
                        // failing here would break workflows that work today — but
                        // it must not be silent.
                        const wiredBy = incomingEdges.filter(
                            (edge) => edge.inputName === inputDef.name
                        );
                        if (wiredBy.length > 0) {
                            console.warn(
                                `Node "${targetNode.id}" (${targetNode.actionId}): input `
                                + `"${inputDef.name}" was wired from `
                                + `${wiredBy.map((e) => `${e.sourceNodeId}.${e.outputName}`).join(', ')} `
                                + 'but that output was not produced. Falling back to workspace '
                                + 'state, which is almost certainly narrower than intended. '
                                + 'Check the source ActionDef declares an output its handler '
                                + 'actually returns, or give it an outputAliases entry.'
                            );
                        }
                        resolvedInputs[inputDef.name] = fallbackValue;
                        continue;
                    }
                }
            }

            // Collect available upstream outputs for error context
            const availableOutputs = [];
            for (const edge of incomingEdges) {
                const result = stepResults.get(edge.sourceNodeId);
                if (result) {
                    availableOutputs.push(...Object.keys(result).map(
                        (k) => `${edge.sourceNodeId}.${k}`
                    ));
                }
            }

            errors.push(
                `Node "${targetNode.id}" (${targetNode.actionId}): required input "${inputDef.name}" `
                + `could not be resolved. Available upstream outputs: ${availableOutputs.length > 0 ? availableOutputs.join(', ') : 'none'}.`
            );
        }
    }

    return { resolvedInputs, errors };
}
